<template>
  <div class="the-slides-create" v-loading.fullscreen="loading">
    <el-row type="flex" align="middle">
      <el-col :span="12">
        <h3>Добавити слайд</h3>
      </el-col>
      <el-col :span="12" class="the-slides-create__actions">
        <el-button
          type="primary"
          size="small"
          icon="el-icon-circle-check"
          @click="addSlide">Зберегти</el-button>
        <el-button
          type="danger"
          size="small"
          plain
          icon="el-icon-circle-close"
          @click="$router.push('/slides')">Скасувати</el-button>
      </el-col>
    </el-row>

    <el-input
      class="the-slides-create__input"
      v-model="newSlide.title"
      type="text"
      placeholder="Заголовок"
      :disabled="newsPostId !== ''"></el-input>

    <el-input
      class="the-slides-create__input"
      v-model="newSlide.btnText"
      type="url"
      placeholder="Текст для кнопки"
      :disabled="newsPostId !== ''"></el-input>

    <el-input
      class="the-slides-create__input"
      v-model="newSlide.btnLink"
      type="url"
      placeholder="Посилання для кнопки"
      :disabled="newsPostId !== ''"></el-input>

    <el-select
      v-if="news.length"
      v-model="newsPostId"
      clearable
      class="the-slides-create__input"
      placeholder="Обрати новину">
      <el-option
        v-for="item in news"
        :key="item._id"
        :label="item.title"
        :value="item._id"></el-option>
    </el-select>

    <div v-show="!newsPostId" class="the-slides-create__editor">
      <div ref="editor"></div>
    </div>

    <p v-show="!newsPostId">Найкраще підійде зображення розміром 1440x860 пікселів</p>

    <el-upload
      v-show="!newsPostId"
      class="the-slides-create__upload"
      action=""
      :http-request="addAttachment"
      :file-list="attachments"
      :show-file-list="false">
      <el-button
        size="small"
        type="primary"
        icon="el-icon-picture">Завантажити зображення</el-button>
      <el-button
        type="danger"
        size="small"
        plain
        :disabled="newSlide.previewPicture === 'default'"
        icon="el-icon-document-delete"
        @click.stop="removePicture">Видалити зображення</el-button>
      <div slot="tip" class="el-upload__tip">Розмiр не бiльше 5 мб</div>
    </el-upload>

    <img
      v-if="newSlide.previewPicture !== 'default'"
      class="the-slides-create__preview-picture"
      :src="newSlide.previewPicture"
      alt="">
  </div>
</template>

<script>
/**
 * Component: the-slides-create - Компонент добавления слайда
 *
 * @version 1.0.0
 */


import {
  apiSetImagePreview,
  apiPostSlide,
  apiGetNews,
} from '@/api';
import { encodeImage } from '@/util';
import { Auth } from 'aws-amplify';
import { mapGetters } from 'vuex';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const DEFAULT_PREVIEW_PICTURE = 'https://kind-challenge-images-1.s3-eu-west-1.amazonaws.com/5a70f2f6-7a21-49e8-ada0-c3e4c18ea298.jpg';

export default {
  name: 'the-slides-create',

  data() {
    return {
      newSlide: {
        title: '',
        content: '',
        btnText: '',
        btnLink: '',
        order: 10,
        previewPicture: 'default',
      },
      attachments: [],
      quill: null,
      loading: false,
      news: [],
      newsPostId: '',
    };
  },

  computed: {
    ...mapGetters({
      user: 'user/user',
      slides: ['slides/slides'],
    }),
  },

  watch: {
    newsPostId(val) {
      if (val) {
        const post = this.news.find(e => e._id === val);

        this.newSlide.newsPostId = val;
        this.newSlide.title = post.title;
        this.newSlide.previewPicture = post.previewPicture === 'default'
          ? DEFAULT_PREVIEW_PICTURE
          : post.previewPicture;
      }
    }
  },

  async mounted() {
    await this.getNewsList();
    await this.initEditor();
    await this.editMode();
  },

  methods: {
    initEditor() {
      this.quill = new Quill(this.$refs.editor, {
        theme: 'snow',
      });

      this.quill.on('text-change', this.handleUserInput);
    },

    async getToken() {
      const userSession = await Auth.userSession(this.user);
      return userSession.accessToken.jwtToken;
    },

    async getNewsList() {
      this.loading = true;
      const token = await this.getToken();
      const { data } = await apiGetNews(token, {
        offset: 0,
        limit: 1000,
      });

      this.news = data.news;
      this.loading = false;
    },

    removePicture() {
      this.newSlide.previewPicture = 'default';
    },

    async addAttachment(files) {
      const token = await this.getToken();
      const image = await encodeImage(files.file);
      const maxSize = 5e+6; // 5mb

      if (files.file.size > maxSize) {
        this.$message({
          type: 'warning',
          message: 'Файл дуже великий. Будь ласка, добавьте фото меншого розмiру.',
        });
      } else {
        try {
          const imageHash = image.split(',')[1];
          const response = await apiSetImagePreview(token, { image: imageHash });

          if (response.status === 200) {
            this.newSlide.previewPicture = response.data.imageUrl;
          }
        } catch (e) {
          console.log(e);
        }
      }
    },

    async addSlide() {
      if (this.slides.length === 3) {
        this.$message('Забагато слайдiв. Краще оновити вже створенi слайди.');
        return;
      }

      const token = await this.getToken();
      this.loading = true;

      if (this.newSlide.previewPicture === 'default') {
        this.newSlide.previewPicture = DEFAULT_PREVIEW_PICTURE;
      }

      try {
        const { status } = await apiPostSlide(token, this.newSlide);

        if (status === 200) {
          this.$router.push('/slides');
        }
      } catch (e) {
        console.log(e);
      }
    },

    handleUserInput() {
      if (this.quill.getLength() > 1) {
        this.newSlide.content = this.quill.root.innerHTML;
      } else {
        this.newSlide.content = '';
      }
    },

    async editMode() {
      const slideId = this.$route.query.id;

      if (slideId) {
        /* eslint-disable */
        const slide = this.slides.find(e => e._id === slideId);
        this.loading = true;

        if (slide.newsPostId) {
          this.newsPostId = slide.newsPostId;
          this.loading = false;
          return;
        }

        this.newSlide = slide;
        this.quill.root.innerHTML = slide.content;
        this.loading = false;
        /* eslint-enable */
      }
    },
  },
};
</script>

<style lang="scss">
@import '~@/scss/styles';

.the-slides-create {
  $this: &;
  padding-bottom: $vp * 2;

  &__input {
    width: 100%;
    margin-bottom: $vp;
  }
  &__actions {
    text-align: right;
  }
  &__editor {

    .ql-container {
      height: 300px;
    }
  }
  &__upload {
    margin-top: $vp;
    margin-bottom: $vp;
    max-width: 500px;
  }
  &__preview-picture {
    max-width: 100%;
  }
}
</style>
